import React, { useState } from "react";
import { GraduationCap, Car, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Iter 739 — "About this neighbourhood" (property detail, under the
 * Location/Schools/Direction card). Two cards fed by
 * GET /classifieds/listings/{id}/neighbourhood:
 *  • Schools: MoE enrolment-zone counts + expandable zoned-school list
 *    (IN ZONE pills linking to the school's official MoE profile).
 *  • Commute: drive time/distance to the nearest CBD. Hidden when null.
 */

const LEVEL_LABELS = {
  primary: "Primary School",
  intermediate: "Intermediate School",
  secondary: "Secondary School",
  composite: "Composite School",
};

export default function NeighbourhoodCards({ geo }) {
  if (!geo) return null;
  const commute = geo.commute;
  return (
    <section className="mt-6" data-testid="neighbourhood-cards">
      <h2 className="font-display text-lg md:text-xl font-bold text-slate-900 mb-3">
        About this neighbourhood
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <SchoolsCard geo={geo} />
        {commute && (
          <div
            className="rounded-2xl bg-white shadow-card p-5 self-start"
            data-testid="neighbourhood-commute-card"
          >
            <h3 className="flex items-center gap-2 font-display font-bold text-slate-900">
              <Car className="w-5 h-5 text-[color:var(--brand-blue)]" /> Commute
            </h3>
            <p className="mt-2 text-sm text-slate-700">
              <span className="font-semibold">{commute.to}</span>
              <span className="mx-1.5 text-slate-300">·</span>
              {commute.minutes} min / {commute.km} km
              {commute.estimate ? <span className="text-slate-400"> (est.)</span> : null}
            </p>
            <p className="mt-1 text-xs text-slate-400">Drive time at a typical hour.</p>
          </div>
        )}
      </div>
    </section>
  );
}

function SchoolsCard({ geo }) {
  const [open, setOpen] = useState(false);
  const zones = geo.zones || [];
  const counts = geo.zone_counts || {};
  const needsAddress = geo.precision !== "address";

  const countLines = Object.entries(LEVEL_LABELS)
    .map(([k, label]) => ({ n: counts[k] || 0, label }))
    .filter((r) => r.n > 0);

  return (
    <div className="rounded-2xl bg-white shadow-card p-5 self-start" data-testid="neighbourhood-schools-card">
      <h3 className="flex items-center gap-2 font-display font-bold" style={{ color: "#0F766E" }}>
        <GraduationCap className="w-5 h-5" /> School zones
      </h3>

      {needsAddress ? (
        <p className="mt-2 text-sm text-slate-500" data-testid="neighbourhood-needs-address">
          School zones need the full street address — ask the seller or agent.
        </p>
      ) : zones.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500" data-testid="neighbourhood-no-zones">
          No enrolment zones cover this address.
        </p>
      ) : (
        <>
          <ul className="mt-2 space-y-0.5">
            {countLines.map((r) => (
              <li key={r.label} className="text-sm text-slate-700 font-semibold">
                {r.n} {r.label}
                {r.n > 1 ? "s" : ""}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="neighbourhood-see-zoned"
            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[color:#0F766E] hover:underline"
          >
            {open ? "Hide zoned schools" : "See zoned schools"}
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {open && (
            <ul className="mt-2 divide-y divide-subtle" data-testid="neighbourhood-zones-list">
              {zones.map((z) => (
                <li key={z.school_id} className="py-2 flex items-center justify-between gap-3">
                  <a
                    href={z.moe_url}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 text-sm font-semibold text-slate-800 hover:text-[color:#0F766E] hover:underline"
                  >
                    {z.name}
                    <span className="block text-xs font-normal text-slate-400">
                      {z.institution_type || z.level_label}
                    </span>
                  </a>
                  <span className="shrink-0 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-black px-2 py-0.5">
                    IN ZONE
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <p className="mt-3 text-[11px] text-slate-400">
        Source: NZ Ministry of Education enrolment zones. Always confirm with the school.
      </p>
    </div>
  );
}
